'use client';

import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Search,
  Trash2,
  Pin,
  X,
  // TODO: Upload — re-enable when CSV-Import feature is planned
  // Upload,
  ClipboardList,
  Heart,
  Copy,
  CheckSquare,
} from 'lucide-react';
import { clsx } from 'clsx';
import { toast } from 'sonner';
import {
  type FavoriteItem,
  type MaterialTemplateItemInput,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useCart, ConfirmDialog } from '@paeffgen/react/crm-documents/feature-material-requests';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import {
  Skeleton,
  EmptyState,
  SelectionBar,
  SelectAllToggle,
  TagFilter,
  TagManager,
  type TagColorId,
  useNavHighlight,
} from '@paeffgen/react/crm-documents/ui';
import {
  parseCsvToDocumentLines,
  useSelection,
} from '@paeffgen/react/crm-documents/util';
import { useFavorites } from './use-favorites';
import { useFavoriteActionMenu } from './use-favorite-action-menu';
import { FavoritesToolbar } from './favorites-toolbar';
import {
  FavoritesSortSheet,
  FAVORITES_SORT_DEFAULT,
  type FavoritesSort,
} from './favorites-sort-sheet';
import { FavoriteCard } from './favorite-card';
import { MultiSelectLabelPopover } from './multi-select-label-popover';
import { SaveToProjectModal } from './save-to-project-modal';

const MAX_PINNED = 6;

export function FavoritesPage() {
  const { contextHeading, adminMutationVars, adminQueryVars } = useAdmin();
  const { markHighlight } = useNavHighlight();
  const {
    favorites,
    userLabels,
    labels,
    colorByLabel,
    loading,
    toggleFavorite,
    updateFavorite,
    addFavorites,
    addUserLabel,
    deleteUserLabel,
    renameUserLabel,
    recolorLabel,
    reorderLabels,
    toggleLabelOnArticle,
  } = useFavorites();

  const { addItem, addItems, removeItem, items: draftItems } = useCart();
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);

  // Derived: which favorite articles are currently on the material list +
  // their current quantities. Single source of truth — survives page refresh,
  // reflects changes from other parts of the app (e.g. Materialanfrage itself).
  const cartArticleNumbers = useMemo(
    () => new Set(draftItems.map((i) => i.articleNumber)),
    [draftItems]
  );
  const cartQuantities = useMemo(() => {
    const map = new Map<string, number>();
    draftItems.forEach((i) => map.set(i.articleNumber, i.quantity));
    return map;
  }, [draftItems]);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState<FavoritesSort>(FAVORITES_SORT_DEFAULT);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [activeLabels, setActiveLabels] = useState<string[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  // Mehrfachauswahl (geteilter Hook — einheitlich mit Belege/Projekte/Artikelsuche).
  // Das X der Aktionsleiste ruft `sel.exit` (Modus aus + Auswahl verwerfen); es gibt
  // bewusst keinen „Fertig"-Button — die Aktionsleiste erscheint sofort im Modus.
  const sel = useSelection();

  // Modals
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  // CSV import
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Favoriten-Anzahl je Label für die Filter-Zähler.
  const labelCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    favorites.forEach((f) =>
      (f.labels ?? []).forEach((l) => {
        counts[l] = (counts[l] ?? 0) + 1;
      })
    );
    return counts;
  }, [favorites]);

  // ── Filtering & Sorting ──────────────────────────────────────────────

  const filteredAndSortedFavorites = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();

    let result = favorites.filter((fav) => {
      const matchesSearch =
        fav.description.toLowerCase().includes(searchLower) ||
        fav.articleNumber.toLowerCase().includes(searchLower);

      const matchesLabels =
        activeLabels.length === 0 ||
        (fav.labels && fav.labels.some((l) => activeLabels.includes(l))) ||
        (activeLabels.includes('pinned:true') && !!fav.pinned);

      return matchesSearch && matchesLabels;
    });

    const dir = sort.direction === 'asc' ? 1 : -1;
    result = [...result].sort((a, b) => {
      switch (sort.field) {
        case 'description':
          return dir * a.description.localeCompare(b.description, 'de');
        case 'articleNumber':
          return dir * a.articleNumber.localeCompare(b.articleNumber, 'de');
        case 'unitPrice':
          return dir * (a.unitPrice - b.unitPrice);
        case 'addedAt': {
          const av = a.addedAt ? Date.parse(a.addedAt) : 0;
          const bv = b.addedAt ? Date.parse(b.addedAt) : 0;
          return dir * (av - bv);
        }
        default:
          return 0;
      }
    });

    return result;
  }, [favorites, searchQuery, sort, activeLabels]);

  // Sichtbare (gefilterte) Artikelnummern — Kandidatenliste für „Alle auswählen".
  const visibleIds = useMemo(
    () => filteredAndSortedFavorites.map((f) => f.articleNumber),
    [filteredAndSortedFavorites]
  );

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleAddToCart = useCallback(
    (article: FavoriteItem) => {
      // Toggle: if already on the list, remove it; else add with current qty.
      // Quantity adjustment is handled via the +/- steppers, so there's no
      // reason to ask for confirmation on a second click.
      if (cartArticleNumbers.has(article.articleNumber)) {
        removeItem(article.articleNumber);
      } else {
        const qty = quantities[article.articleNumber] || 1;
        addItem(article.articleNumber, qty);
      }
    },
    [cartArticleNumbers, quantities, addItem, removeItem]
  );

  const handleAddSelectedToCart = useCallback(() => {
    const itemsToAdd = sel.selectedIds.map((articleNumber) => ({
      articleNumber,
      quantity: quantities[articleNumber] || 1,
    }));
    addItems(itemsToAdd);
    sel.clear();
  }, [sel, quantities, addItems]);

  const handleRemoveSelected = useCallback(() => {
    sel.selectedIds.forEach((articleNumber) => {
      toggleFavorite(articleNumber);
    });
    sel.clear();
  }, [sel, toggleFavorite]);

  const handleTogglePin = useCallback(
    (article: FavoriteItem, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!article.pinned) {
        const pinnedCount = favorites.filter((f) => f.pinned).length;
        if (pinnedCount >= MAX_PINNED) {
          return;
        }
      }
      updateFavorite(article.articleNumber, { pinned: !article.pinned });
    },
    [favorites, updateFavorite]
  );

  // Auswahl als Projekt-Positionen (Mengen: Anfrage-Menge → manuell → 1).
  const projectItems = useMemo<MaterialTemplateItemInput[]>(
    () =>
      sel.selectedIds.map((articleNumber) => ({
        articleNumber,
        quantity:
          cartQuantities.get(articleNumber) ?? quantities[articleNumber] ?? 1,
      })),
    [sel.selectedIds, cartQuantities, quantities]
  );

  // CSV Import
  const handleCsvImport = useCallback(
    async (file: File) => {
      try {
        // Simple lookup that returns null for all articles —
        // the server will resolve full article data via addFavorites
        const lines = await parseCsvToDocumentLines(file, () => null);
        const articleNumbers = lines
          .map((l) => l.articleNumber)
          .filter((x): x is string => x !== null);
        if (articleNumbers.length > 0) {
          await addFavorites(articleNumbers);
          // Set imported quantities
          setQuantities((prev) => {
            const next = { ...prev };
            lines.forEach((line) => {
              if (line.articleNumber !== null) {
                next[line.articleNumber] = line.quantity ?? 1;
              }
            });
            return next;
          });
        }
      } catch {
        // Error handling: the query refetch will show current state
      }
    },
    [addFavorites]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleCsvImport(file);
        // Reset the input so the same file can be selected again
        e.target.value = '';
      }
    },
    [handleCsvImport]
  );

  // ── Label-Verwaltung (TagManager) ────────────────────────────────────

  const handleRenameLabel = useCallback(
    async (oldName: string, newName: string) => {
      try {
        await renameUserLabel(oldName, newName);
        setActiveLabels((prev) => prev.map((l) => (l === oldName ? newName : l)));
        toast.success(`Label umbenannt zu „${newName}"`);
      } catch {
        toast.error('Label konnte nicht umbenannt werden');
      }
    },
    [renameUserLabel]
  );

  const handleDeleteLabel = useCallback(
    async (name: string) => {
      try {
        await deleteUserLabel(name);
        setActiveLabels((prev) => prev.filter((l) => l !== name));
        toast.success(`Label „${name}" gelöscht`);
      } catch {
        toast.error('Label konnte nicht gelöscht werden');
      }
    },
    [deleteUserLabel]
  );

  const handleRecolorLabel = useCallback(
    (name: string, color: TagColorId) => {
      recolorLabel(name, color).catch(() =>
        toast.error('Farbe konnte nicht gespeichert werden')
      );
    },
    [recolorLabel]
  );

  const handleReorderLabels = useCallback(
    (orderedNames: string[]) => {
      reorderLabels(orderedNames).catch(() =>
        toast.error('Reihenfolge konnte nicht gespeichert werden')
      );
    },
    [reorderLabels]
  );

  const handleCreateLabel = useCallback(
    async (name: string, color: TagColorId) => {
      try {
        await addUserLabel(name, color);
        toast.success(`Label „${name}" angelegt`);
      } catch {
        toast.error('Label konnte nicht angelegt werden');
      }
    },
    [addUserLabel]
  );

  // ── Aktionsmenü (geteilte Orchestrierung inkl. Beleg-Dialog) ──────────
  const { openActionMenu, overlays } = useFavoriteActionMenu({
    favorites,
    labels,
    updateFavorite,
    toggleFavorite,
    toggleLabelOnArticle,
    addUserLabel,
    onAddToCart: handleAddToCart,
    belegSearchTerm: searchQuery,
  });

  // ── Loading state ────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6 animate-in fade-in duration-300 pb-20 md:pb-0">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <Skeleton className="w-14 h-14 rounded-xl" />
            <div className="space-y-2">
              <Skeleton className="w-48 h-7" />
              <Skeleton className="w-96 h-4" />
            </div>
          </div>
        </div>
        <Skeleton className="w-full h-14 rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-72 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <>
    <div className="space-y-6 animate-in fade-in duration-300 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-orange-50 text-primary rounded-xl shrink-0 mt-0.5">
            <Heart size={28} fill="currentColor" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-secondary">
              {contextHeading('Favoriten')}
            </h1>
            <p className="text-text-muted mt-1 text-sm md:text-base max-w-2xl">
              Ihre persönliche Merkliste für einzelne Artikel, die Sie immer
              wieder benötigen — unabhängig von konkreten Projekten. Ideal
              für{' '}
              <span className="font-medium text-secondary">
                Verbrauchsmaterialien
              </span>{' '}
              oder{' '}
              <span className="font-medium text-secondary">
                Stammsortiment
              </span>
              .
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* TODO: CSV-Import-Button — Feature wird zu späterem Zeitpunkt durchgeplant */}
          {/*
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 shadow-sm"
          >
            <Upload size={18} />
            Importieren
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileChange}
          />
          */}
        </div>
      </div>

      {/* Empty State */}
      {favorites.length === 0 ? (
        <EmptyState
          icon={Heart}
          title="Noch keine Favoriten angelegt"
          description="Speichern Sie einzelne Artikel als Favoriten, um sie später ohne langes Suchen schnell zur Anfrage hinzuzufügen. Nutzen Sie dafür das Herz-Symbol an den Artikeln."
          className="bg-white rounded-xl shadow-sm border border-gray-200 mt-8"
        />
      ) : (
        <>
          {/* Controls Bar (Suche + Sortierung) */}
          <FavoritesToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sort={sort}
            onSortChange={setSort}
            onOpenSort={() => setIsSortOpen(true)}
          />

          {/* Label-Filter: „Alle" → „Angepinnt" → Labels → „Verwalten".
              Immer sichtbar — auch ohne Label, damit „Verwalten" das erste
              Label anlegen kann (Reihenfolge wie bei den Projekten). */}
          <div className="pt-2">
            <TagFilter
              labels={userLabels}
              counts={labelCounts}
              total={favorites.length}
              activeLabels={activeLabels}
              colorByLabel={colorByLabel}
              onToggle={(label) =>
                setActiveLabels((prev) =>
                  prev.includes(label)
                    ? prev.filter((l) => l !== label)
                    : [...prev, label]
                )
              }
              onClear={() => setActiveLabels([])}
              onOpenManage={() => setManageOpen(true)}
              afterAll={
                <button
                  type="button"
                  onClick={() =>
                    setActiveLabels((prev) =>
                      prev.includes('pinned:true')
                        ? prev.filter((l) => l !== 'pinned:true')
                        : [...prev, 'pinned:true']
                    )
                  }
                  aria-pressed={activeLabels.includes('pinned:true')}
                  className={clsx(
                    'inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[13px] font-semibold border whitespace-nowrap transition-colors',
                    activeLabels.includes('pinned:true')
                      ? 'border-primary text-primary bg-orange-50'
                      : 'border-gray-200 text-gray-600 bg-white hover:border-gray-300 hover:bg-gray-50'
                  )}
                >
                  <Pin
                    size={13}
                    fill={
                      activeLabels.includes('pinned:true')
                        ? 'currentColor'
                        : 'none'
                    }
                  />
                  Angepinnt
                </button>
              }
            />
          </div>

          {/* Aktionsleiste (mobil + Desktop) = geteilte SelectionBar weiter unten. */}

          {/* Results Count & Select All */}
          <div className="flex justify-between items-center px-1">
            <div className="flex items-center gap-4">
              {!sel.selectMode && (
                <button
                  type="button"
                  onClick={sel.enter}
                  aria-label="Artikel auswählen"
                  className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  <CheckSquare size={16} />
                  Auswählen
                </button>
              )}
              {sel.selectMode && (
                <SelectAllToggle
                  checked={sel.allSelected(visibleIds)}
                  indeterminate={sel.someSelected(visibleIds)}
                  onToggle={() => sel.toggleAll(visibleIds)}
                  disabled={visibleIds.length === 0}
                  onExit={sel.exit}
                />
              )}
              {searchQuery && (
                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md border border-blue-100 animate-in fade-in slide-in-from-left-2">
                  <Search size={12} />
                  <span className="text-xs font-semibold">
                    Suche aktiv
                  </span>
                </div>
              )}
            </div>
            <span className="text-sm text-gray-500">
              {filteredAndSortedFavorites.length}{' '}
              {filteredAndSortedFavorites.length === 1
                ? 'Ergebnis'
                : 'Ergebnisse'}
            </span>
          </div>

          {/* Grid / List View */}
          {filteredAndSortedFavorites.length === 0 ? (
            <div className="text-center py-12 px-6 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col items-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
                <Search size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                Keine Ergebnisse gefunden
              </h3>
              <p className="text-gray-500 max-w-xs mx-auto mb-6">
                Für Ihre Suche nach{' '}
                <span className="font-semibold text-gray-900">
                  &quot;{searchQuery}&quot;
                </span>{' '}
                konnten wir leider keine Favoriten finden.
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="px-6 py-2 bg-primary text-white font-bold rounded-lg hover:bg-primary-hover shadow-md transition-all active:scale-95 flex items-center gap-2"
              >
                <X size={18} strokeWidth={2.5} />
                Suche löschen
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredAndSortedFavorites.map((article) => (
                <FavoriteCard
                  key={article.articleNumber}
                  article={article}
                  colorByLabel={colorByLabel}
                  selectMode={sel.selectMode}
                  isSelected={sel.isSelected(article.articleNumber)}
                  inCart={cartArticleNumbers.has(article.articleNumber)}
                  onToggleSelect={sel.toggle}
                  onTogglePin={handleTogglePin}
                  onAddToCart={handleAddToCart}
                  onMenu={openActionMenu}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <SaveToProjectModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        items={projectItems}
        selectedCount={sel.count}
        queryVars={adminQueryVars}
        mutationVars={adminMutationVars}
        onSaved={() => {
          markHighlight('projekte');
          sel.clear();
          setIsTemplateModalOpen(false);
        }}
      />

      <ConfirmDialog
        isOpen={isRemoveConfirmOpen}
        onClose={() => setIsRemoveConfirmOpen(false)}
        onConfirm={handleRemoveSelected}
        title="Favoriten entfernen"
        message={
          sel.count === 1 ? (
            <>
              Möchten Sie den{' '}
              <span className="font-semibold text-gray-900">
                ausgewählten Artikel
              </span>{' '}
              wirklich aus den Favoriten{' '}
              <span className="font-semibold text-gray-900">entfernen</span>?
            </>
          ) : (
            <>
              Möchten Sie die{' '}
              <span className="font-semibold text-gray-900">
                {sel.count} ausgewählten Artikel
              </span>{' '}
              wirklich aus den Favoriten{' '}
              <span className="font-semibold text-gray-900">entfernen</span>?
            </>
          )
        }
        confirmLabel="Ja, entfernen"
        cancelLabel="Abbrechen"
        variant="danger"
      />

      <SelectionBar
        count={sel.count}
        open={sel.selectMode}
        onClear={sel.exit}
      >
        <div
          className={clsx(
            sel.count === 0 && 'opacity-40 pointer-events-none',
          )}
          aria-disabled={sel.count === 0}
        >
          <MultiSelectLabelPopover
            selectedArticleNumbers={sel.selectedIds}
            onClose={() => {
              /* noop */
            }}
            direction="up"
            compact
            variant="dark"
          />
        </div>
        <button
          type="button"
          disabled={sel.count === 0}
          onClick={() => setIsTemplateModalOpen(true)}
          className="px-2.5 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold inline-flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/10"
          title="Als Projekt speichern"
        >
          <Copy size={14} /> Projekt
        </button>
        <button
          type="button"
          disabled={sel.count === 0}
          onClick={() => setIsRemoveConfirmOpen(true)}
          aria-label="Aus Favoriten löschen"
          title="Aus Favoriten löschen"
          className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-white/10 hover:bg-red-500/20 text-red-300 hover:text-red-200 transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/10"
        >
          <Trash2 size={14} />
        </button>
        <button
          type="button"
          disabled={sel.count === 0}
          onClick={handleAddSelectedToCart}
          className="px-2.5 py-2 rounded-lg bg-primary hover:bg-primary-hover text-white text-xs font-bold inline-flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-primary"
          title="Alle zur Anfrage hinzufügen"
        >
          <ClipboardList size={14} /> Anfrage
        </button>
      </SelectionBar>
    </div>

      {/* Overlays bewusst AUSSERHALB des space-y-6-Containers: dessen
          Sibling-Margin würde fixed-Sheets/Backdrops sonst um 16px vom unteren
          Viewport-Rand wegschieben (Lücke unter dem Sheet). */}
      <FavoritesSortSheet
        open={isSortOpen}
        onClose={() => setIsSortOpen(false)}
        value={sort}
        onChange={setSort}
      />

      {/* Labels verwalten — mobil BottomSheet, Desktop Modal */}
      <TagManager
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        labels={labels}
        counts={labelCounts}
        usageNoun={{ singular: 'Artikel', plural: 'Artikeln' }}
        onRename={handleRenameLabel}
        onRecolor={handleRecolorLabel}
        onReorder={handleReorderLabels}
        onCreate={handleCreateLabel}
        onDelete={handleDeleteLabel}
      />

      {/* Aktionsmenü (Sheet/Popover) + Beleg-Dialog — geteilte Orchestrierung.
          Bewusst hier (außerhalb des space-y-6-Containers) gerendert. */}
      {overlays}
    </>
  );
}
